/**
 * Session Report Viewer
 * 
 * Displays the new structured session report format with timeline, highlights, and final outcome
 * Provides a clean, organized view of cooking session data
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Heading, Text, Card, Section, Modal } from './ui';
import sessionReportService from '../services/sessionReportService';
import { uploadImage, getRecipeById, getDraftRecipe, getCookingSessionById, updateRecipe } from '../services/api';
import PostSharingModal from './PostSharingModal';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a'
};

// Photo component with error handling
const PhotoDisplay = ({ photo, className, alt }) => {
    const [imageError, setImageError] = useState(false);
    
    // Check if it's a blob URL (which are likely expired)
    const isBlobUrl = photo.url && photo.url.startsWith('blob:');
    
    // console.log('PhotoDisplay rendering:', { photo, isBlobUrl, imageError });
    
    if (imageError || isBlobUrl) {
        return (
            <div className={`${className} bg-gray-200 flex items-center justify-center text-gray-500 text-xs`}>
                📷 Photo unavailable
            </div>
        );
    }
    
    return (
        <img
            src={photo.url}
            alt={alt}
            className={className}
            onError={() => {
                console.warn('Failed to load photo:', photo.url);
                setImageError(true);
            }}
        />
    );
};

// Utility function to clean report data and remove blob URLs
const cleanReportData = (reportData) => {
    if (!reportData) return reportData;
    
    const cleanPhotos = (photos) => {
        if (!photos || !Array.isArray(photos)) return photos;
        const originalCount = photos.length;
        const cleanedPhotos = photos.filter(photo => {
            if (!photo.url) return false;
            if (photo.url.startsWith('blob:')) {
                // console.log('Filtering out blob URL:', photo.url);
                return false;
            }
            return true;
        });
        if (originalCount !== cleanedPhotos.length) {
            // console.log(`Cleaned ${originalCount - cleanedPhotos.length} blob URLs from photos`);
        }
        return cleanedPhotos;
    };
    
    const cleanTimeline = (timeline) => {
        if (!timeline || !Array.isArray(timeline)) return timeline;
        return timeline.map(entry => ({
            ...entry,
            photos: cleanPhotos(entry.photos)
        }));
    };
    
    const cleanHighlights = (highlights) => {
        if (!highlights || !Array.isArray(highlights)) return highlights;
        return highlights.map(highlight => ({
            ...highlight,
            photos: cleanPhotos(highlight.photos)
        }));
    };
    
    const cleanFinalOutcome = (outcome) => {
        if (!outcome) return outcome;
        
        let dishPhoto = outcome.dish_photo;
        if (dishPhoto && dishPhoto.url && dishPhoto.url.startsWith('blob:')) {
            // console.log('Filtering out blob URL from dish photo:', dishPhoto.url);
            dishPhoto = null;
        }
        
        return {
            ...outcome,
            dish_photo: dishPhoto
        };
    };
    
    return {
        ...reportData,
        timeline: cleanTimeline(reportData.timeline),
        highlights: cleanHighlights(reportData.highlights),
        final_outcome: cleanFinalOutcome(reportData.final_outcome)
    };
};

const SessionReportViewer = ({ sessionId, user, onUpdate, chefSummariesReport }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showPostSharingModal, setShowPostSharingModal] = useState(false);
    const [recipeSteps, setRecipeSteps] = useState([]);
    const [expandedSteps, setExpandedSteps] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    
    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (sessionId && user) {
            loadSessionReport();
            loadSessionAndRecipeData();
        }
    }, [sessionId, user]);

    const loadSessionAndRecipeData = async () => {
        try {
            if (!user) return;
            
            const token = await user.getIdToken();
            
            // Get session data to find recipe_id
            const session = await getCookingSessionById(sessionId, token);
            console.log('Session data:', session);
            
            // For recipe-based sessions, use recipe_instructions from session
            if (session.recipe_instructions && Array.isArray(session.recipe_instructions)) {
                // Convert recipe_instructions array to steps format
                const steps = session.recipe_instructions.map((instruction, index) => ({
                    title: instruction.startsWith('Prep ') || instruction.startsWith('Step ') 
                        ? instruction.split(':')[0] 
                        : `Step ${index + 1}`,
                    instruction: instruction,
                    body: instruction
                }));
                setRecipeSteps(steps);
                return;
            }
            
            // For draft-based sessions, get recipe or draft data to extract steps
            let recipe = null;
            if (session.draft_id) {
                recipe = await getDraftRecipe(session.draft_id, token);
                if (recipe && recipe.steps) {
                    setRecipeSteps(recipe.steps);
                }
            }
        } catch (err) {
            console.error('Error loading session/recipe data:', err);
            // Don't set error here, as timeline can still work without recipe steps
        }
    };

    const loadSessionReport = async () => {
        try {
            setLoading(true);
            setError(null);
            
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            const token = await user.getIdToken();
            if (!token) {
                throw new Error('Failed to get authentication token');
            }
            
            // console.log('Loading session report with token:', token.substring(0, 20) + '...');
            const result = await sessionReportService.getSessionReport(sessionId, token);
            
            let reportData = null;
            if (result && result.data) {
                // Clean the report data to remove blob URLs
                reportData = cleanReportData(result.data);
                // console.log('Cleaned session report data:', reportData);
            } else {
                console.warn('No session report data received, creating empty report');
                reportData = {
                    timeline: [],
                    highlights: [],
                    final_outcome: {
                        rating: null,
                        comments: '',
                        dish_photo: null
                    }
                };
            }
            
            // Merge Chef Summaries report if available
            if (chefSummariesReport) {
                // console.log('📝 Merging Chef Summaries report:', chefSummariesReport);
                
                // Merge timeline entries
                if (chefSummariesReport.timeline && chefSummariesReport.timeline.length > 0) {
                    reportData.timeline = [...(reportData.timeline || []), ...chefSummariesReport.timeline];
                }
                
                // Merge highlights
                if (chefSummariesReport.highlights && chefSummariesReport.highlights.length > 0) {
                    reportData.highlights = [...(reportData.highlights || []), ...chefSummariesReport.highlights];
                }
                
                // Merge final outcome
                if (chefSummariesReport.final_outcome) {
                    if (chefSummariesReport.final_outcome.rating) {
                        reportData.final_outcome.rating = chefSummariesReport.final_outcome.rating;
                    }
                    if (chefSummariesReport.final_outcome.comments) {
                        reportData.final_outcome.comments = chefSummariesReport.final_outcome.comments;
                    }
                }
                
                // console.log('📝 Merged report data:', reportData);
            }
            
            // console.log('Timeline data:', reportData.timeline);
            // console.log('Highlights data:', reportData.highlights);
            // console.log('Final outcome data:', reportData.final_outcome);
            setReport(reportData);
        } catch (err) {
            console.error('Error loading session report:', err);
            setError(`Failed to load session report: ${err.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const handleImageUpload = async (file, type, targetId = null) => {
        if (!file) return;
        
        try {
            setUploading(true);
            
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            const token = await user.getIdToken();
            if (!token) {
                throw new Error('Failed to get authentication token');
            }
            
            // Upload the image
            const uploadedImageUrl = await uploadImage(file, token);
            // console.log('Uploaded image URL:', uploadedImageUrl);
            
            // Create photo entry
            const photoEntry = {
                url: uploadedImageUrl,
                timestamp: new Date().toISOString(),
                caption: file.name,
                trigger: 'manual_upload'
            };
            // console.log('Created photo entry:', photoEntry);
            
            // Update the report based on type
            let updatedReport = { ...report };
            
            if (type === 'timeline' && targetId) {
                // Initialize timeline array if it doesn't exist
                if (!updatedReport.timeline) {
                    updatedReport.timeline = [];
                }
                
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    // Ensure photos array exists
                    if (!updatedReport.timeline[timelineIndex].photos) {
                        updatedReport.timeline[timelineIndex].photos = [];
                    }
                    updatedReport.timeline[timelineIndex].photos.push(photoEntry);
                } else if (targetId === 'default-timeline-entry') {
                    // Create new entry from default
                    const newEntry = {
                        id: `timeline-${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        phase: 'prep',
                        step_title: 'Timeline Entry',
                        notes: '',
                        photos: [photoEntry],
                        status: 'ok'
                    };
                    updatedReport.timeline.push(newEntry);
                }
            } else if (type === 'highlight' && targetId) {
                // Initialize highlights array if it doesn't exist
                if (!updatedReport.highlights) {
                    updatedReport.highlights = [];
                }
                
                const highlightIndex = updatedReport.highlights.findIndex(highlight => highlight.id === targetId);
                if (highlightIndex !== -1) {
                    // Ensure photos array exists
                    if (!updatedReport.highlights[highlightIndex].photos) {
                        updatedReport.highlights[highlightIndex].photos = [];
                    }
                    updatedReport.highlights[highlightIndex].photos.push(photoEntry);
                } else if (targetId === 'default-highlight-entry') {
                    // Create new entry from default
                    const newHighlight = {
                        id: `highlight-${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        type: 'success',
                        title: 'Highlight',
                        description: '',
                        photos: [photoEntry]
                    };
                    updatedReport.highlights.push(newHighlight);
                }
            } else if (type === 'final_outcome') {
                // Ensure final_outcome exists
                if (!updatedReport.final_outcome) {
                    updatedReport.final_outcome = {};
                }
                // Set as dish photo
                updatedReport.final_outcome.dish_photo = photoEntry;
                
                // If this is a recipe session and recipe has no images, add final photo to recipe
                // CRITICAL: Only update the SPECIFIC recipe from the session, not any other recipes in the lineage
                try {
                    const session = await getCookingSessionById(sessionId, token);
                    
                    if (session && session.recipe_id) {
                        // Store the specific recipe ID to ensure we only update this one
                        const specificRecipeId = session.recipe_id;
                        
                        const currentRecipe = await getRecipeById(specificRecipeId);
                        
                        // Verify we got the correct recipe
                        if (currentRecipe.id !== specificRecipeId) {
                            console.error('❌ SessionReportViewer: Recipe ID mismatch!', {
                                expected: specificRecipeId,
                                received: currentRecipe.id
                            });
                            return; // Don't update if IDs don't match
                        }
                        
                        console.log('🔍 SessionReportViewer: Checking recipe for image update:', {
                            sessionId,
                            recipeId: specificRecipeId,
                            recipeName: currentRecipe.name,
                            chain_root_id: currentRecipe.chain_root_id,
                            parent_recipe_id: currentRecipe.parent_recipe_id,
                            hasImages: !!(currentRecipe.images && currentRecipe.images.length > 0),
                            imageCount: currentRecipe.images?.length || 0,
                            uploadedImageUrl
                        });
                        
                        if (!currentRecipe.images || currentRecipe.images.length === 0) {
                            const formDataForUpdate = new FormData();
                            formDataForUpdate.append('name', currentRecipe.name || '');
                            formDataForUpdate.append('category', currentRecipe.category || '');
                            if (currentRecipe.cooking_time) {
                                formDataForUpdate.append('cooking_time', currentRecipe.cooking_time);
                            }
                            
                            // Add the final photo URL as existingImages (backend expects URLs in existingImages, not images)
                            formDataForUpdate.append('existingImages', JSON.stringify([uploadedImageUrl]));
                            
                            console.log('🔍 SessionReportViewer: Updating SPECIFIC recipe with final photo:', {
                                sessionId,
                                recipeId: specificRecipeId,
                                recipeName: currentRecipe.name,
                                photoUrl: uploadedImageUrl
                            });
                            
                            // Update ONLY this specific recipe by ID
                            await updateRecipe(specificRecipeId, formDataForUpdate, token);
                            
                            console.log('✅ SessionReportViewer: Recipe updated successfully with final photo:', {
                                recipeId: specificRecipeId
                            });
                        } else {
                            console.log('ℹ️ SessionReportViewer: Recipe already has images, skipping update:', {
                                recipeId: specificRecipeId,
                                imageCount: currentRecipe.images.length
                            });
                        }
                    } else {
                        console.log('ℹ️ SessionReportViewer: No recipe_id in session, skipping update');
                    }
                } catch (error) {
                    console.error('❌ SessionReportViewer: Error updating recipe with final dish photo:', error);
                    // Don't fail the whole operation if recipe update fails
                }
            }
            
            // Save the updated report
            // console.log('Saving updated report:', updatedReport);
            await sessionReportService.updateSessionReport(sessionId, updatedReport, token);
            setReport(updatedReport);
            // console.log('Report updated successfully');
            
            // Don't call onUpdate here - that's for navigation only
            
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (event, type, targetId = null) => {
        const file = event.target.files[0];
        if (file) {
            handleImageUpload(file, type, targetId);
        }
        // Reset the input
        event.target.value = '';
    };

    const handleDeletePhoto = async (type, targetId = null, photoIndex = null) => {
        if (!user) {
            alert('User not authenticated');
            return;
        }

        try {
            const token = await user.getIdToken();
            let updatedReport = { ...report };
            
            if (type === 'timeline' && targetId !== null && photoIndex !== null) {
                // Remove photo from timeline entry
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    updatedReport.timeline[timelineIndex].photos.splice(photoIndex, 1);
                }
            } else if (type === 'highlight' && targetId !== null && photoIndex !== null) {
                // Remove photo from highlight
                const highlightIndex = updatedReport.highlights.findIndex(highlight => highlight.id === targetId);
                if (highlightIndex !== -1) {
                    updatedReport.highlights[highlightIndex].photos.splice(photoIndex, 1);
                }
            } else if (type === 'final_outcome') {
                // Remove dish photo
                updatedReport.final_outcome.dish_photo = null;
            }
            
            // Save the updated report
            await sessionReportService.updateSessionReport(sessionId, updatedReport, token);
            setReport(updatedReport);
            
            // Don't call onUpdate here - that's for navigation only
            
        } catch (error) {
            console.error('Error deleting photo:', error);
            alert('Failed to delete photo. Please try again.');
        }
    };

    // Immediate state update (no debounce)
    const handleTextEditImmediate = useCallback((type, targetId, field, value) => {
        setReport(prevReport => {
            if (!prevReport) return prevReport;
            
            const updatedReport = { ...prevReport };
            
            if (type === 'timeline') {
                if (!updatedReport.timeline) {
                    updatedReport.timeline = [];
                }
                
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    updatedReport.timeline[timelineIndex] = {
                        ...updatedReport.timeline[timelineIndex],
                        [field]: value
                    };
                } else if (targetId === 'default-timeline-entry') {
                    const newEntry = {
                        id: `timeline-${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        phase: 'prep',
                        step_title: 'Timeline Entry',
                        notes: field === 'notes' ? value : '',
                        photos: [],
                        status: 'ok'
                    };
                    newEntry[field] = value;
                    updatedReport.timeline.push(newEntry);
                }
            } else if (type === 'final_outcome') {
                if (!updatedReport.final_outcome) {
                    updatedReport.final_outcome = {};
                }
                updatedReport.final_outcome[field] = value;
            } else if (type === 'additional_notes') {
                updatedReport.additional_notes = value;
            }
            
            return updatedReport;
        });
    }, []);

    // Debounced save to API
    const debouncedSave = useCallback(async (reportData) => {
        if (!user || !sessionId) return;
        
        try {
            const token = await user.getIdToken();
            await sessionReportService.updateSessionReport(sessionId, reportData, token);
        } catch (error) {
            console.error('Error saving session report:', error);
        }
    }, [user, sessionId]);

    // Handle text edit with debouncing
    const handleTextEdit = useCallback((type, targetId, field, value) => {
        // Update state immediately for responsive UI
        handleTextEditImmediate(type, targetId, field, value);
        
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Set new timer to save after 1 second of inactivity
        debounceTimerRef.current = setTimeout(() => {
            setReport(currentReport => {
                if (currentReport) {
                    debouncedSave(currentReport);
                }
                return currentReport;
            });
        }, 1000);
    }, [handleTextEditImmediate, debouncedSave]);
    
    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleEditSummary = () => {
        setIsEditing(true);
    };

    const handleUpdateSummary = async () => {
        try {
            if (!user || !sessionId || !report) return;
            
            const token = await user.getIdToken();
            await sessionReportService.updateSessionReport(sessionId, report, token);
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating summary:', error);
            alert('Failed to update summary. Please try again.');
        }
    };

    const handleSubmitReflection = async () => {
        try {
            // Show the post sharing modal
            setShowPostSharingModal(true);
        } catch (error) {
            console.error('Error completing reflection:', error);
            alert('Failed to complete reflection. Please try again.');
        }
    };

    const handlePostCreated = (createdPost) => {
        // console.log('Post created successfully:', createdPost);
        // Navigate back to the recipe or home page after posting or skipping
        if (onUpdate) onUpdate();
    };

    const getPhaseColor = (phase) => {
        const colors = {
            'preparation': 'bg-blue-100 text-blue-800',
            'cooking': 'bg-red-100 text-red-800',
            'resting': 'bg-yellow-100 text-yellow-800',
            'serving': 'bg-green-100 text-green-800',
            'cleanup': 'bg-gray-100 text-gray-800',
            'paused': 'bg-orange-100 text-orange-800',
            'completed': 'bg-purple-100 text-purple-800'
        };
        return colors[phase] || 'bg-gray-100 text-gray-800';
    };

    const getStatusColor = (status) => {
        const colors = {
            'ok': 'bg-green-100 text-green-800',
            'delayed': 'bg-yellow-100 text-yellow-800',
            'needs_attention': 'bg-red-100 text-red-800',
            'completed': 'bg-green-100 text-green-800',
            'skipped': 'bg-gray-100 text-gray-800',
            'modified': 'bg-blue-100 text-blue-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Text className="text-neutral-900 opacity-60">Loading session report...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <Text className="text-red-600">{error}</Text>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="bg-neutral-0 hairline p-6">
                <div className="text-center py-8">
                    <Text className="text-neutral-900 opacity-60 mb-4">No session report found</Text>
                    <Text className="text-sm text-neutral-900 opacity-40">
                        This session may not have generated a report yet, or the data is still being processed.
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Main Card */}
            <div className="bg-neutral-0 hairline p-6">
                {/* Session Info */}
                <div className="text-center mb-6">
                    <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                        <span>Started: {formatTimestamp(report.start_time)}</span>
                        {report.end_time && (
                            <>
                                <span>•</span>
                                <span>Ended: {formatTimestamp(report.end_time)}</span>
                            </>
                        )}
                        {report.duration_seconds && (
                            <>
                                <span>•</span>
                                <span>Duration: {Math.floor(report.duration_seconds / 60)} minutes</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Section Separator */}
                <div className="border-t border-neutral-200 mb-6"></div>

                {/* Step-Based Timeline Section */}
                <TimelineView 
                    timeline={report.timeline}
                    recipeSteps={recipeSteps}
                    report={report}
                    setReport={setReport}
                    formatTimestamp={formatTimestamp}
                    getPhaseColor={getPhaseColor}
                    getStatusColor={getStatusColor}
                    getPhaseDisplayName={sessionReportService.getPhaseDisplayName}
                    getStatusDisplay={sessionReportService.getStatusDisplay}
                    uploading={uploading}
                    handleFileSelect={handleFileSelect}
                    handleDeletePhoto={handleDeletePhoto}
                    handleTextEdit={handleTextEdit}
                    sessionId={sessionId}
                    user={user}
                    isEditing={isEditing}
                />

                {/* Section Separator */}
                <div className="border-t border-neutral-200 my-8"></div>

                {/* Final Dish Photo Section */}
                <div className="space-y-4">
                    <Heading level={3} className="text-xl font-medium mb-2" style={{ color: colorTokens.oxfordBlue }}>
                        Final Dish Photo
                    </Heading>
                    <Text className="text-sm opacity-60 mb-4">
                        Share a photo of your final dish
                    </Text>
                    
                    {report.final_outcome?.dish_photo && (
                        <div className="relative inline-block">
                            <img
                                src={report.final_outcome.dish_photo.url || (typeof report.final_outcome.dish_photo === 'string' ? report.final_outcome.dish_photo : '')}
                                alt="Final dish"
                                className="w-64 h-64 object-cover hairline"
                                onError={(e) => {
                                    console.warn('Failed to load final dish photo:', report.final_outcome.dish_photo);
                                    e.target.style.display = 'none';
                                }}
                            />
                            {isEditing && (
                                <button
                                    onClick={() => handleDeletePhoto('final_outcome')}
                                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-sm hover:opacity-60 transition-opacity font-mono bg-red-500 text-white"
                                    title="Remove photo"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    )}
                    
                    {!report.final_outcome?.dish_photo && isEditing && (
                        <div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileSelect(e, 'final_outcome')}
                                className="hidden"
                                id="final-dish-upload"
                                disabled={uploading}
                            />
                            <label
                                htmlFor="final-dish-upload"
                                className="inline-flex items-center px-6 py-3 text-sm font-mono uppercase tracking-wider text-white hover:opacity-60 transition-opacity cursor-pointer"
                                style={{ backgroundColor: colorTokens.oxfordBlue }}
                            >
                                {uploading ? 'Uploading...' : '📷 Add Final Dish Photo'}
                            </label>
                        </div>
                    )}
                </div>

                {/* Section Separator */}
                <div className="border-t border-neutral-200 my-8"></div>

                {/* Additional Notes Section */}
                <AdditionalNotesView 
                    report={report}
                    handleTextEdit={handleTextEdit}
                    isEditing={isEditing}
                />

                {/* Section Separator */}
                <div className="border-t border-neutral-200 my-6"></div>

                {/* Edit/Update Summary Button */}
                <div className="flex justify-center">
                    {!isEditing ? (
                        <button
                            onClick={handleEditSummary}
                            className="px-6 py-3 font-mono text-sm uppercase tracking-wider text-white hover:opacity-60 transition-opacity"
                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                        >
                            Edit Summary
                        </button>
                    ) : (
                        <button
                            onClick={handleUpdateSummary}
                            className="px-6 py-3 font-mono text-sm uppercase tracking-wider text-white hover:opacity-60 transition-opacity"
                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                        >
                            Update Summary
                        </button>
                    )}
                </div>
            </div>

            {/* Post Sharing Modal */}
            <PostSharingModal
                isOpen={showPostSharingModal}
                onClose={() => setShowPostSharingModal(false)}
                sessionId={sessionId}
                sessionReport={{
                    ...report,
                    fork_id: report?.session_data?.fork_id,
                    recipe_title: report?.session_data?.recipe_title
                }}
                onPostCreated={handlePostCreated}
            />

        </div>
    );
};

// Timeline View Component - Step-Based Organization
const TimelineView = ({ timeline, recipeSteps, report, setReport, formatTimestamp, getPhaseColor, getStatusColor, getPhaseDisplayName, getStatusDisplay, uploading, handleFileSelect, handleDeletePhoto, handleTextEdit, sessionId, user, isEditing }) => {
    // Group timeline entries by step index
    const timelineByStep = {};
    if (timeline && timeline.length > 0) {
        timeline.forEach(entry => {
            const stepIndex = entry.step_index || entry.stepIndex || 0;
            if (!timelineByStep[stepIndex]) {
                timelineByStep[stepIndex] = [];
            }
            timelineByStep[stepIndex].push(entry);
        });
    }

    // Use recipe steps from session - only show steps from the live session
    const getStepsCount = () => {
        if (recipeSteps && recipeSteps.length > 0) {
            return recipeSteps.length;
        }
        // If no recipe steps, return 0 (don't show arbitrary steps)
        return 0;
    };

    const stepsCount = getStepsCount();
    
    // Create array of steps
    const steps = Array.from({ length: stepsCount }, (_, index) => {
        let title = `Step ${index + 1}`;
        
        // Try to get title from recipe steps first
        if (recipeSteps && recipeSteps[index]) {
            title = recipeSteps[index].title || recipeSteps[index].instruction || title;
        } else {
            // Fallback to timeline entry title
            const timelineEntry = timeline?.find(e => (e.step_index || e.stepIndex) === index);
            if (timelineEntry && timelineEntry.step_title) {
                title = timelineEntry.step_title;
            }
        }
        
        return {
            index: index,
            title: title,
            entries: timelineByStep[index] || []
        };
    });

    const createTimelineEntry = async (stepIndex) => {
        const newEntry = {
            id: `timeline-${Date.now()}`,
            timestamp: new Date().toISOString(),
            step_index: stepIndex,
            step_title: steps[stepIndex].title,
            phase: 'cooking',
            notes: '',
            photos: [],
            status: 'ok'
        };
        
        // Update local report state
        const updatedReport = { ...report };
        if (!updatedReport.timeline) {
            updatedReport.timeline = [];
        }
        updatedReport.timeline.push(newEntry);
        setReport(updatedReport);
        
        // Save to backend
        try {
            const token = await user.getIdToken();
            await sessionReportService.updateSessionReport(sessionId, updatedReport, token);
        } catch (error) {
            console.error('Error saving new timeline entry:', error);
        }
        
        // Expand the step
        if (typeof setExpandedSteps === 'function') {
            setExpandedSteps(prev => ({ ...prev, [stepIndex]: true }));
        } else {
            console.warn('setExpandedSteps is not available');
        }
    };

    // Format time to show only hours and minutes
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    return (
        <div className="space-y-4">
            <Heading level={3} className="text-xl font-medium mb-6" style={{ color: colorTokens.oxfordBlue }}>
                Cooking Timeline
            </Heading>
            <Text className="text-sm opacity-60 mb-6">
                Review each step and add photos or notes about your experience
            </Text>
            
            {/* Step-by-Step Timeline - Horizontal Scrollable */}
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin' }}>
                {steps.map((step, stepIndex) => {
                    const hasEntries = step.entries.length > 0;
                    // Check if step has any text content (notes)
                    const hasTextContent = hasEntries && step.entries.some(entry => entry.notes && entry.notes.trim().length > 0);
                    
                    return (
                        <div key={stepIndex} className="flex-shrink-0 w-80">
                            {!hasEntries || !hasTextContent ? (
                                /* Minimal View for Empty Step or Step Without Text */
                                <div className="flex flex-col items-center space-y-3 p-4 bg-neutral-50 hairline min-h-[200px]">
                                    <div 
                                        className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-white font-mono text-xs hairline"
                                        style={{ backgroundColor: colorTokens.oxfordBlue }}
                                        title={step.title}
                                    >
                                        {stepIndex + 1}
                                    </div>
                                    <Text className="text-xs text-neutral-900 opacity-60 text-center">
                                        {step.title}
                                    </Text>
                                    {isEditing && (
                                        <button
                                            onClick={() => createTimelineEntry(stepIndex)}
                                            className="w-6 h-6 flex items-center justify-center text-white hover:opacity-60 transition-opacity hairline"
                                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                                            title={`Add text or photos for ${step.title}`}
                                        >
                                            <span className="text-sm font-mono">+</span>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                /* Large Card for Steps with Content */
                                <div className="bg-neutral-50 hairline min-h-[200px]">
                                    {/* Step Header */}
                                    <div className="flex items-center justify-between p-4 bg-white border-b border-neutral-900 border-opacity-20">
                                        <div className="flex items-center space-x-4 flex-1">
                                            <div 
                                                className="w-10 h-10 flex-shrink-0 flex items-center justify-center text-white font-mono text-sm"
                                                style={{ backgroundColor: colorTokens.oxfordBlue }}
                                            >
                                                {stepIndex + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <Text className="font-medium text-neutral-900 text-base">
                                                    {step.title}
                                                </Text>
                                                <Text className="text-xs text-neutral-900 opacity-60 font-mono uppercase tracking-wider mt-1">
                                                    {step.entries.length} {step.entries.length === 1 ? 'entry' : 'entries'}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Content Area */}
                                    <div className="p-4 space-y-4 bg-white">
                                        {step.entries.map((entry, entryIndex) => (
                                            <div key={entry.id} className="bg-neutral-50 hairline p-4 space-y-3">
                                                {/* Entry Header with Timestamp */}
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex gap-2 flex-wrap">
                                                        {entry.phase && (
                                                            <span className={`px-2 py-1 text-xs font-mono uppercase tracking-wider ${getPhaseColor(entry.phase)}`}>
                                                                {getPhaseDisplayName(entry.phase)}
                                                            </span>
                                                        )}
                                                        {entry.status && (
                                                            <span className={`px-2 py-1 text-xs font-mono uppercase tracking-wider ${getStatusColor(entry.status)}`}>
                                                                {getStatusDisplay(entry.status).name}
                                                            </span>
                                                        )}
                                                        {entry.type === 'photo' && (
                                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-purple-100 text-purple-800">
                                                                📷 Photo
                                                            </span>
                                                        )}
                                                        {entry.type === 'check_in' && (
                                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-blue-100 text-blue-800">
                                                                ✓ Check-in
                                                            </span>
                                                        )}
                                                        {entry.trigger === 'chef_reminder' && (
                                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-green-100 text-green-800">
                                                                👨‍🍳 Chef Reminder
                                                            </span>
                                                        )}
                                                    </div>
                                                    <Text className="text-xs font-mono text-neutral-900 opacity-60">
                                                        {formatTime(entry.timestamp)}
                                                    </Text>
                                                </div>
                                                
                                                {/* Photos */}
                                                {entry.photos && entry.photos.length > 0 && (
                                                    <div className="flex gap-2 flex-wrap">
                                                        {entry.photos.map((photo, photoIndex) => (
                                                            <div key={photoIndex} className="relative">
                                                                <PhotoDisplay
                                                                    photo={photo}
                                                                    alt={photo.caption || `Photo ${photoIndex + 1}`}
                                                                    className="w-24 h-24 object-cover hairline"
                                                                />
                                                                {isEditing && (
                                                                    <button
                                                                        onClick={() => handleDeletePhoto('timeline', entry.id, photoIndex)}
                                                                        className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center text-xs hover:opacity-60 transition-opacity font-mono bg-red-500 text-white"
                                                                        title="Delete photo"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Image Data (base64) - Show as image if present */}
                                                {entry.imageData && (
                                                    <div className="relative inline-block">
                                                        <img 
                                                            src={entry.imageData.startsWith('data:') ? entry.imageData : `data:image/jpeg;base64,${entry.imageData}`}
                                                            alt="Timeline entry" 
                                                            className="w-24 h-24 object-cover hairline"
                                                        />
                                                    </div>
                                                )}
                                                
                                                {/* Notes */}
                                                <div>
                                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 mb-2">Notes:</Text>
                                                    {isEditing ? (
                                                        <textarea
                                                            value={entry.notes || ''}
                                                            onChange={(e) => handleTextEdit('timeline', entry.id, 'notes', e.target.value)}
                                                            placeholder="Add notes about this step..."
                                                            className="w-full p-3 hairline text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2"
                                                            style={{ focusRingColor: colorTokens.oxfordBlue }}
                                                            rows={3}
                                                        />
                                                    ) : (
                                                        <Text className="text-sm text-neutral-900 whitespace-pre-wrap">
                                                            {entry.notes || 'No notes'}
                                                        </Text>
                                                    )}
                                                </div>
                                                
                                                {/* Upload button */}
                                                {isEditing && (
                                                    <div>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleFileSelect(e, 'timeline', entry.id)}
                                                            className="hidden"
                                                            id={`timeline-upload-${entry.id}`}
                                                            disabled={uploading}
                                                        />
                                                        <label
                                                            htmlFor={`timeline-upload-${entry.id}`}
                                                            className="inline-flex items-center px-3 py-2 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors cursor-pointer"
                                                        >
                                                            {uploading ? 'Uploading...' : '📷 Add Photo'}
                                                        </label>
                                                    </div>
                                                )}
                                                
                                                {/* Chef Notes */}
                                                {entry.chef_notes && (
                                                    <div className="bg-blue-50 p-3 hairline" style={{ borderLeftWidth: '4px', borderLeftColor: '#3b82f6' }}>
                                                        <Text className="text-sm text-blue-800">
                                                            <strong>Chef Note:</strong> {entry.chef_notes}
                                                        </Text>
                                                    </div>
                                                )}
                                                
                                                {/* Chef Suggestions */}
                                                {entry.chef_suggestions && entry.chef_suggestions.length > 0 && (
                                                    <div className="bg-green-50 p-3 hairline" style={{ borderLeftWidth: '4px', borderLeftColor: '#10b981' }}>
                                                        <Text className="text-sm font-medium text-green-900 mb-2">
                                                            Chef Suggestions:
                                                        </Text>
                                                        <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                                                            {entry.chef_suggestions.map((suggestion, idx) => (
                                                                <li key={idx}>{suggestion}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        
                                        {/* Add Another Entry Button */}
                                        {isEditing && (
                                            <button
                                                onClick={() => createTimelineEntry(stepIndex)}
                                                className="w-full py-3 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                            >
                                                + Add Another Photo or Note
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Additional Notes View Component
const AdditionalNotesView = ({ report, handleTextEdit, isEditing }) => {
    const additionalNotes = report.additional_notes || '';
    
    return (
        <div className="space-y-6">
            <div>
                <Heading level={3} className="text-xl font-medium mb-2" style={{ color: colorTokens.oxfordBlue }}>
                    Additional Notes
                </Heading>
                <Text className="text-sm opacity-60 mb-4">
                    Add any extra observations, variations, or important details from your cooking session
                </Text>
            </div>
            
            <div>
                {isEditing ? (
                    <textarea
                        value={additionalNotes}
                        onChange={(e) => handleTextEdit('additional_notes', null, 'additional_notes', e.target.value)}
                        placeholder="Add any additional notes, observations, or things you want to remember from this session..."
                        className="w-full p-4 hairline text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2"
                        style={{ focusRingColor: colorTokens.oxfordBlue }}
                        rows={6}
                    />
                ) : (
                    <Text className="text-sm text-neutral-900 whitespace-pre-wrap p-4 bg-neutral-50 hairline min-h-[150px]">
                        {additionalNotes || 'No additional notes'}
                    </Text>
                )}
            </div>
        </div>
    );
};


export default SessionReportViewer;
